import type { TeamStatsMember } from './teamStats'

// Roster status derivation (spec: coach-team-overview). Derived client-side from
// fields already in the team stats response, like rolePlayStatus.tsx does for Module2.
export type RosterStatus = 'top' | 'ontrack' | 'stalled' | 'risk'

const DAY_MS = 24 * 60 * 60 * 1000

export const daysSince = (iso: string | null, now: number): number | null => {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return Math.floor((now - t) / DAY_MS)
}

// AT RISK: no sessions or inactive 14+ days. STALLED: inactive 7-13 days.
// TOP: single highest avg score among members active within 7 days with >=1 session.
export const deriveRosterStatuses = (members: TeamStatsMember[], now: number): Map<number, RosterStatus> => {
  const statuses = new Map<number, RosterStatus>()
  let topUserId: number | null = null
  let topScore = -1

  for (const m of members) {
    const inactiveDays = daysSince(m.lastActiveAt, now)
    if (m.totalSessions === 0 || inactiveDays === null || inactiveDays >= 14) {
      statuses.set(m.userId, 'risk')
    } else if (inactiveDays >= 7) {
      statuses.set(m.userId, 'stalled')
    } else {
      statuses.set(m.userId, 'ontrack')
      if (m.avgScore > topScore) {
        topScore = m.avgScore
        topUserId = m.userId
      }
    }
  }

  if (topUserId !== null) statuses.set(topUserId, 'top')
  return statuses
}

export const formatLastActive = (iso: string | null, now: number): string => {
  const days = daysSince(iso, now)
  if (days === null) return 'Never'
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days >= 7 && days < 14) return '1 week ago'
  return `${days} days ago`
}
